import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import AudiobookLandingView from './AudiobookLandingView';
import { AudiobookLandingPublic, parseAudiobookLanding } from './audiobookLandingPublic';
import { track, trackLandingPixelView, leaveLandingPixelView } from '../../helpers/analytics';

// This WordPress host serves REST through the query route; Apache returns 404 for /wp-json/.
const SOURCE = 'https://www.alexandersbooks.com/?rest_route=/bookish-audio/v1/landing/';

function setPageMetadata(landing: AudiobookLandingPublic): () => void {
  const previousTitle = document.title;
  document.title = `${landing.title} - Audiobook | Bookish Boyfriend`;
  const canonical = document.createElement('link');
  canonical.rel = 'canonical';
  canonical.href = landing.bookishLandingUrl;
  document.head.appendChild(canonical);
  return () => { canonical.remove(); document.title = previousTitle; };
}

export default function AudiobookLanding() {
  const { slug = '' } = useParams();
  const [landing, setLanding] = useState<AudiobookLandingPublic | null>(null);
  const [state, setState] = useState<'loading' | 'missing' | 'error'>('loading');

  useEffect(() => {
    if (!slug || slug.length > 200 || slug.includes('/')) {
      setLanding(null); setState('missing'); return;
    }
    const controller = new AbortController();
    setLanding(null); setState('loading');
    fetch(SOURCE + encodeURIComponent(slug), {
      credentials: 'omit', mode: 'cors', cache: 'no-store', signal: controller.signal,
    }).then(async (response) => {
      if (response.status === 404) { setState('missing'); return; }
      if (!response.ok) throw new Error('Audiobook landing unavailable');
      const data = parseAudiobookLanding(await response.json(), slug);
      const canonical = new URL(data.bookishLandingUrl);
      const segments = canonical.pathname.split('/').filter(Boolean);
      if (canonical.origin !== 'https://bookishboyfriend.com' ||
          segments.length !== 2 || segments[0] !== 'audiobook' ||
          decodeURIComponent(segments[1]) !== decodeURIComponent(slug) ||
          canonical.search || canonical.hash) {
        throw new Error('Invalid audiobook landing URL');
      }
      setLanding(data);
    }).catch((error) => {
      if (error.name !== 'AbortError') setState('error');
    });
    trackLandingPixelView();
    return () => { controller.abort(); leaveLandingPixelView(); };
  }, [slug]);

  useEffect(() => landing ? setPageMetadata(landing) : undefined, [landing]);

  if (landing) return <AudiobookLandingView landing={landing}
    onRetailerClick={(vendor) => track('audiobook_retailer_click', { source: vendor })}
    onAuthorClick={() => track('audiobook_author_site_click', { source: 'author_website' })} />;
  return <main className="audiobook-landing" role="status" aria-live="polite">
    <div className="audiobook-landing__inner">
      {state === 'loading' ? 'Loading audiobook…'
        : state === 'missing' ? 'This audiobook landing page was not found.'
        : 'This audiobook landing page is temporarily unavailable. Please try again later.'}
    </div>
  </main>;
}
